import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, PencilSimple, Trash, Check, X } from "@phosphor-icons/react";
import PlayerDetailsModal from "./PlayerDetailsModal";
import { useSettings } from "@/context/SettingsContext";
import { Header, AddBtn, Modal, In, Sel, SaveBtn, IconBtn, DivisionTabs } from "./shared";

export default function TeamsTab() {
  const { settings } = useSettings();
  const DIVISIONS = settings.divisions || [];
  const [teams, setTeams] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", coach: "", division: "Open", logo_url: "", primary_color: "#FF5722" });
  const [loading, setLoading] = useState(false);
  const [division, setDivision] = useState("Open");

  const load = useCallback(() => api.get("/teams").then((r) => setTeams(r.data)), []);
  useEffect(() => { load(); }, [load]);

  const divisionTeams = useMemo(
    () => teams.filter((t) => (t.division || "Open") === division),
    [teams, division]
  );

  const openNew = () => { setEditing(null); setForm({ name: "", coach: "", division, logo_url: "", primary_color: "#FF5722" }); setOpen(true); };
  const openEdit = (t) => { setEditing(t); setForm({ name: t.name, coach: t.coach || "", division: t.division || "Open", logo_url: t.logo_url || "", primary_color: t.primary_color || "#FF5722" }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      if (editing) await api.put(`/teams/${editing.id}`, form);
      else await api.post("/teams", form);
      toast.success("Team saved"); setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  const del = async (id) => { if (!window.confirm("Delete this team and its players?")) return; await api.delete(`/teams/${id}`); toast.success("Deleted"); load(); };

  // Roster management
  const [rosterTeam, setRosterTeam] = useState(null);
  const [roster, setRoster] = useState([]);
  const emptyRP = { name: "", number: "", position: "" };
  const [rp, setRp] = useState(emptyRP);
  const [rpLoading, setRpLoading] = useState(false);

  const loadRoster = useCallback((teamId) => api.get(`/players?team_id=${teamId}`).then((r) => setRoster(r.data)), []);
  const openRoster = (t) => { setRosterTeam(t); setRp(emptyRP); loadRoster(t.id); };

  const addRosterPlayer = async (e) => {
    e.preventDefault(); setRpLoading(true);
    try {
      await api.post("/players", { name: rp.name, team_id: rosterTeam.id, number: String(rp.number ?? "").trim() || null, position: rp.position });
      toast.success("Player added to roster"); setRp(emptyRP); loadRoster(rosterTeam.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setRpLoading(false); }
  };

  const removeRosterPlayer = async (id) => {
    if (!window.confirm("Remove this player from the roster?")) return;
    await api.delete(`/players/${id}`); toast.success("Player removed"); loadRoster(rosterTeam.id);
  };

  // Inline jersey-number editing
  const [editNumId, setEditNumId] = useState(null);
  const [editNumVal, setEditNumVal] = useState("");
  const [savingNum, setSavingNum] = useState(false);
  const [detailsPlayer, setDetailsPlayer] = useState(null);
  const startEditNum = (p) => { setEditNumId(p.id); setEditNumVal(p.number ?? ""); };
  const cancelEditNum = () => { setEditNumId(null); setEditNumVal(""); };
  const saveNum = async (p) => {
    const n = String(editNumVal ?? "").trim();
    if (n !== "" && (!/^[0-9]+$/.test(n) || Number(n) > 9000)) { toast.error("Jersey number must be 00–9000"); return; }
    const val = n === "" ? null : n;
    if (val === (p.number ?? null)) { cancelEditNum(); return; }
    setSavingNum(true);
    try {
      const res = await api.patch(`/players/${p.id}/number`, { number: val });
      setRoster((prev) => prev.map((x) => (x.id === p.id ? { ...x, number: res.data.number } : x)));
      toast.success("Jersey number updated successfully.");
      cancelEditNum();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingNum(false); }
  };

  // Inline player-name editing
  const [editNameId, setEditNameId] = useState(null);
  const [editNameVal, setEditNameVal] = useState("");
  const [savingName, setSavingName] = useState(false);
  const startEditName = (p) => { setEditNameId(p.id); setEditNameVal(p.name || ""); };
  const cancelEditName = () => { setEditNameId(null); setEditNameVal(""); };
  const saveName = async (p) => {
    const nm = editNameVal.trim();
    if (!nm) { toast.error("Name can't be empty"); return; }
    if (nm === (p.name || "")) { cancelEditName(); return; }
    setSavingName(true);
    try {
      const res = await api.patch(`/players/${p.id}/name`, { name: nm });
      setRoster((prev) => prev.map((x) => (x.id === p.id ? { ...x, name: res.data.name } : x)));
      toast.success("Player name updated successfully.");
      cancelEditName();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSavingName(false); }
  };

  return (
    <div data-testid="teams-tab">
      <Header title="Teams" action={<AddBtn onClick={openNew} label="Add Team" testid="add-team-btn" />} />
      <DivisionTabs value={division} onChange={setDivision} prefix="teams" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {divisionTeams.map((t) => (
          <div key={t.id} className="border border-white/10 bg-[#121212] p-5" data-testid="team-card">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 flex items-center justify-center text-black font-display text-2xl shrink-0" style={{ background: t.primary_color }}>
                {t.logo_url ? <img src={t.logo_url} alt="" className="w-full h-full object-cover" /> : t.name.charAt(0)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-xl uppercase truncate">{t.name}</div>
                <div className="text-white/40 text-xs truncate">{t.coach ? `Coach: ${t.coach}` : "—"}</div>
                <div className="mt-1"><span className="text-[10px] uppercase tracking-wide bg-[#FF5722]/20 text-[#FF5722] px-2 py-0.5">{t.division || "Open"}</span></div>
              </div>
              <IconBtn onClick={() => openEdit(t)} testid="edit-team-btn"><PencilSimple size={18} /></IconBtn>
              <IconBtn onClick={() => del(t.id)} danger testid="delete-team-btn"><Trash size={18} /></IconBtn>
            </div>
            <button
              data-testid="manage-roster-btn"
              onClick={() => openRoster(t)}
              className="mt-4 w-full flex items-center justify-center gap-2 border border-white/15 hover:border-[#FF5722] hover:text-[#FF5722] text-white/70 text-xs font-bold uppercase tracking-wide py-2.5 transition-all duration-200"
            >
              <Users size={16} /> Manage Roster
            </button>
          </div>
        ))}
        {divisionTeams.length === 0 && <p className="text-white/40">No teams in the {division} division yet.</p>}
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit Team" : "Add Team"}>
        <form onSubmit={save} className="space-y-4">
          <In label="Team Name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="team-name-input" />
          <In label="Coach" value={form.coach} onChange={(e) => setForm({ ...form, coach: e.target.value })} />
          <Sel label="Division" value={form.division} onChange={(e) => setForm({ ...form, division: e.target.value })} data-testid="team-division-select">
            {DIVISIONS.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </Sel>
          <In label="Logo URL (optional)" value={form.logo_url} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} />
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wide text-white/60 mb-1.5">Team Color</label>
            <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })} className="w-20 h-10 bg-transparent border border-white/15 cursor-pointer" />
          </div>
          <SaveBtn loading={loading} testid="save-team-btn" />
        </form>
      </Modal>

      {/* Roster modal */}
      <Modal open={!!rosterTeam} onClose={() => setRosterTeam(null)} title={rosterTeam ? `${rosterTeam.name} — Roster` : "Roster"}>
        <div data-testid="roster-modal">
          <div className="border border-white/10 mb-5">
            {roster.length === 0 && <p className="text-white/40 text-sm px-4 py-4">No players on this roster yet.</p>}
            {roster.map((p) => (
              <div key={p.id} className="flex items-center justify-between px-4 py-3 border-b border-white/5 last:border-0" data-testid="roster-player-row">
                <div className="flex items-center gap-3 min-w-0">
                  {editNumId === p.id ? (
                    <div className="flex items-center gap-1 shrink-0">
                      <input
                        type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} autoFocus
                        value={editNumVal}
                        onChange={(e) => setEditNumVal(e.target.value.replace(/[^0-9]/g, ""))}
                        onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveNum(p); } if (e.key === "Escape") cancelEditNum(); }}
                        data-testid={`roster-jersey-input-${p.id}`}
                        className="w-16 bg-[#1A1A1A] border border-[#FF5722] px-2 py-1 text-sm text-[#FF5722] font-display tabular-nums focus:outline-none"
                      />
                      <button type="button" onClick={() => saveNum(p)} disabled={savingNum} data-testid={`roster-jersey-save-${p.id}`} className="p-1.5 text-[#4ade80] hover:bg-white/10 transition-colors disabled:opacity-50" title="Save"><Check size={16} weight="bold" /></button>
                      <button type="button" onClick={cancelEditNum} data-testid={`roster-jersey-cancel-${p.id}`} className="p-1.5 text-white/50 hover:text-white hover:bg-white/10 transition-colors" title="Cancel"><X size={16} weight="bold" /></button>
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => startEditNum(p)}
                      data-testid={`roster-jersey-edit-${p.id}`}
                      title="Edit jersey number"
                      className="group flex items-center gap-1 font-display text-lg text-[#FF5722] tabular-nums min-w-[2.5rem] hover:opacity-80 transition-opacity"
                    >
                      <span>{p.number != null && p.number !== "" ? `#${p.number}` : "#—"}</span>
                      <PencilSimple size={12} className="text-white/30 group-hover:text-[#FF5722] transition-colors" />
                    </button>
                  )}
                  <div className="min-w-0">
                    <button type="button" onClick={() => setDetailsPlayer(p)} data-testid={`roster-name-edit-${p.id}`} title="Open player details" className="group flex items-center gap-1.5 font-semibold text-sm truncate hover:text-[#FF5722] transition-colors">
                      <span className="truncate uppercase">{p.name}</span>
                      {p.status === "inactive" && <span className="text-[9px] uppercase font-bold bg-white/10 text-white/50 px-1.5 py-0.5 shrink-0">Inactive</span>}
                      <PencilSimple size={12} className="text-white/30 group-hover:text-[#FF5722] transition-colors shrink-0" />
                    </button>
                    {p.position && <div className="text-white/40 text-xs uppercase">{p.position}</div>}
                  </div>
                </div>
                <IconBtn onClick={() => removeRosterPlayer(p.id)} danger testid="remove-roster-player-btn"><Trash size={16} /></IconBtn>
              </div>
            ))}
          </div>
          <form onSubmit={addRosterPlayer} className="space-y-3">
            <h4 className="text-xs font-bold uppercase tracking-wide text-white/60">Add Player to Roster</h4>
            <In label="Player Name" required value={rp.name} onChange={(e) => setRp({ ...rp, name: e.target.value })} data-testid="roster-player-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <In label="Jersey #" type="text" inputMode="numeric" pattern="[0-9]*" maxLength={4} value={rp.number} onChange={(e) => setRp({ ...rp, number: e.target.value.replace(/[^0-9]/g, "") })} data-testid="roster-player-number-input" />
              <In label="Position" value={rp.position} onChange={(e) => setRp({ ...rp, position: e.target.value })} data-testid="roster-player-position-input" />
            </div>
            <button type="submit" disabled={rpLoading} data-testid="add-roster-player-btn" className="w-full bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide py-3 transition-all duration-200">
              {rpLoading ? "Adding..." : "Add Player"}
            </button>
          </form>
        </div>
      </Modal>

      {detailsPlayer && (
        <PlayerDetailsModal
          player={detailsPlayer}
          onClose={() => setDetailsPlayer(null)}
          onSaved={(updated) => {
            setDetailsPlayer(updated);
            setRoster((prev) => prev.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)).filter((x) => !rosterTeam || x.team_id === rosterTeam.id));
          }}
        />
      )}
    </div>
  );
}
